import { ChangeEvent, JSX } from "react";
import { useWindowSize } from "hooks/useWindowSize";
import { MultiValue, SingleValue } from "react-select";
import { Chip } from "components/DesignSystem/Chips/Chips";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { Filter } from "components/DesignSystem/Filter/Filter";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import { BooksDateFilter } from "components/Filters/BooksDateFilter";
import { UpdateFilterFunction } from "hooks/useFilter";
import { RecognitionWaterfall } from "store/apis/recognitionWaterfall";
import { formatTimeToMonthYear } from "utils/formatDate";
import {
  NOOP,
  PAGE_SIZE_MENU_STYLES,
  PAGE_SIZE_OPTIONS,
  PERIOD_FILTER,
} from "./constants";

export type WaterfallFilters = {
  START_DATE: string;
  END_DATE: string;
  SELECT_PERIOD: string;
};

type WaterfallToolbarProps = {
  searchPlaceholder: string;
  searchTerm: string;
  onSearchChange: (event: ChangeEvent<HTMLInputElement>) => void;
  filters: WaterfallFilters;
  onFilterUpdate: UpdateFilterFunction<WaterfallFilters>;
  data?: RecognitionWaterfall;
  pageNum: number;
  onPageNumChange: (pageNum: number) => void;
  pageSize: number;
  onPageSizeChange: (pageSize: string) => void;
  isFetching: boolean;
};

type PageSizeOption = SingleValue<OptionData> | MultiValue<OptionData>;

const isSingleOption = (option: PageSizeOption): option is OptionData =>
  Boolean(option) && !Array.isArray(option);

export const WaterfallToolbar = ({
  searchPlaceholder,
  searchTerm,
  onSearchChange,
  filters,
  onFilterUpdate,
  data,
  pageNum,
  onPageNumChange,
  pageSize,
  onPageSizeChange,
  isFetching,
}: WaterfallToolbarProps): JSX.Element => {
  const { isMobile } = useWindowSize();

  const handlePageSizeChange = (option: PageSizeOption) => {
    if (isSingleOption(option)) {
      onPageSizeChange(option.value);
    }
  };

  return (
    <div className="t-flex t-justify-between t-items-center t-gap-2 sm:t-flex-col sm:t-items-stretch">
      <div className="t-flex t-items-center t-gap-2">
        <Search
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={onSearchChange}
          isLoading={isFetching}
          className="t-w-72 sm:t-w-auto sm:t-flex-1 sm:t-min-w-0"
        />
        <Filter.Root
          defaultValue={PERIOD_FILTER}
          hideDivider={isMobile}
          title={
            isMobile
              ? filters.START_DATE && filters.END_DATE
                ? "(1)"
                : null
              : undefined
          }
          capsule={
            filters.START_DATE && filters.END_DATE ? (
              <span className="t-contents sm:t-hidden">
                <Chip
                  onRemove={NOOP}
                  isRemovable={false}
                  isActive
                  filterType={PERIOD_FILTER}
                >
                  {formatTimeToMonthYear(filters.START_DATE)}
                  {" to "}
                  {formatTimeToMonthYear(filters.END_DATE)}
                </Chip>
              </span>
            ) : null
          }
        >
          <Filter.Portal>
            <Filter.List>
              <Filter.ListItem value={PERIOD_FILTER}>Period</Filter.ListItem>
            </Filter.List>
            <Filter.Body value={PERIOD_FILTER} block>
              <BooksDateFilter
                pickerType="month"
                values={filters}
                updateFilter={onFilterUpdate}
                skipMaxEndDate
              />
            </Filter.Body>
          </Filter.Portal>
        </Filter.Root>
      </div>
      {data && (
        <div className="t-flex t-items-center t-gap-1 sm:t-justify-end">
          <PaginationHandler
            page_num={pageNum}
            onPageNumChange={onPageNumChange}
            per_page={data.per_page}
            total_count={data.total_count}
            total_pages={data.total_pages}
            isLoading={isFetching}
          />
          {/* Own page-size picker: the pager's built-in one goes up to 300, which this view can't afford */}
          <Combobox
            aria-label="Items per page"
            menuPortalTarget={document.body}
            options={PAGE_SIZE_OPTIONS}
            onChange={handlePageSizeChange}
            isClearable={false}
            isSearchable={false}
            value={{ label: String(pageSize), value: String(pageSize) }}
            styles={PAGE_SIZE_MENU_STYLES}
            size="xsmall"
          />
        </div>
      )}
    </div>
  );
};
